// MST
import { types, cast, flow, detach } from 'mobx-state-tree'

// Interfaces
import {
  IProfileApiNullResponse,
  IProfileApiProfileResponse,
} from 'interfaces/back-office/personal-data/profile/profile-api.interface'
import { IProfileGetAttrs, IProfileUpdateAttrs } from 'interfaces/back-office/personal-data/profile/profile-view.interface'
import { ISharedCommonLoadingAttrs } from 'interfaces/shared/shared-common.interface'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'

// Lodash
import cloneDeep from 'lodash/cloneDeep'

// Utils
import { commonUtils_phoneNumber } from 'utils/common/common.utils'
import { withHelper_companyDataProps, withHelper_profileDataProps } from 'models/extensions/with-helper'
import { withHelper_commonLoadingModel, withHelper_commonMapLoading } from 'models/extensions/helper'

// Constant
import { LOADING } from 'constant/shared.constant'

// Types
const { model, optional } = types

// Loading
const { ACTION, BASE } = LOADING

// Profile Data Model
const ProfileData = model('ProfileData').props(withHelper_profileDataProps)

// Company Data Model
const CompanyData = model('CompanyData').props(withHelper_companyDataProps)

// Base Model
const Profile = model('Profile')
  .props({
    profile_loading: withHelper_commonLoadingModel,
    profile_profileData: optional(ProfileData, {}),
    profile_companyData: optional(CompanyData, {}),
  })
  .extend(withEnvironment)
  .actions(self => ({
    profile_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.profile_loading, payload)

      detach(self.profile_loading)
      self.profile_loading = cast(map)
    },
    profile_SET_PROFILE: (payload: IProfileApiProfileResponse['res']['data']): void => {
      if (payload.profileData)
        self.profile_profileData = cast(
          cloneDeep({
            ...payload?.profileData,
            phoneNumber: commonUtils_phoneNumber(payload.profileData.phoneNumber || ''),
          }),
        )
      if (payload.companyData)
        self.profile_companyData = cast(
          cloneDeep({
            ...payload?.companyData,
          }),
        )
    },
    profile_CLEAR_PROFILE: (): void => {
      self.profile_profileData = cast({})
      self.profile_companyData = cast({})
    },
  }))
  .actions(self => ({
    /**
     * @description Get profile
     *
     * @param {IProfileGetAttrs} payload
     *
     * @return {any} any
     */
    profile_fetchProfile: flow(function* (payload: IProfileGetAttrs) {
      self.profile_SET_LOADING({ type: BASE, value: true })

      const response: IProfileApiProfileResponse = yield self.environment.profileApi.profile_getProfile({
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        if (response.res.data) self.profile_SET_PROFILE(response.res.data)

        // Check if user passing callback when request successfully
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Check if response failed
        if (payload?.onError) payload.onError()
      }

      self.profile_SET_LOADING({ type: BASE, value: false })
    }),

    /**
     * @description Update profile
     *
     * @param {IProfileUpdateAttrs} payload
     *
     * @return {any} any
     */
    profile_updateProfile: flow(function* (payload: IProfileUpdateAttrs) {
      self.profile_SET_LOADING({ type: ACTION, value: true })

      const response: IProfileApiNullResponse = yield self.environment.profileApi.profile_updateProfile({
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })

      // Check if user passing callback when request successfully
      if (response.ok && payload?.onSuccess) payload.onSuccess()

      if (!response.ok && payload?.onError) payload.onError()

      self.profile_SET_LOADING({ type: ACTION, value: false })
    }),
  }))

export const createProfileStoreDefaultModel = () => optional(Profile, {})
